'use client';
import { postRequest } from '@/api/helpers';
import { routes } from '@/api/routes';
import { isEmpty, validateEmail } from '@/utils/common-helpers';
import { clsx } from 'clsx';
import Head from 'next/head';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import PhoneInput from 'react-phone-input-2';
import { toast } from 'react-toastify';
import CustomAsyncPaginateStatic from './async-paginate-static';

type ContactFormProps = {
    wrapperClassName?: string;
    title?: string;
    subtitle?: React.ReactNode;
    requestFromPage?: string;
};

const ContactForm = ({
    wrapperClassName = 'md:col-span-7 lg:col-span-7',
    title = 'Get in touch to discuss your ideas',
    subtitle = "Please don't hesitate to ask us for a quote or seek advice.",
    requestFromPage,
}: ContactFormProps) => {
    // const location = usePathname()

    const initialValidation = {
        name: '',
        email: '',
        phone: '',
        message: '',
        attachment: '',
        inquire_for: '',
        budget: '',
        time_frame: '',
        company_name: '',
        zip_code: '',
        preferred_date: '',
    };
    const initialValue = {
        name: '',
        email: '',
        phone: '',
        inquire_for: '',
        budget: null,
        timeFrame: null,
        message: '',
        attachment: '',
        company_name: '',
        zip_code: '',
        preferred_date: '',
    };

    let data = [];

    data.push(
        { label: 'Get a Quote', value: 'get_a_quote' },
        {
            label: 'Collaboration Opportunity',
            value: 'collaboration_opportunity',
        },
        { label: 'Apply for Job', value: 'apply_for_job' },
        { label: 'Apply for Internship', value: 'apply_for_internship' },
        { label: 'General Inquiry', value: 'general_inquiry' },
    );

    const budgetData = [
        { label: 'Less than $10,000', value: '1' },
        { label: '$10,000 - $25,000', value: '2' },
        { label: '$25,000 - $50,000', value: '3' },
        { label: '$50,000 - $100,000', value: '4' },
        { label: '$100,000 and above', value: '5' },
        { label: 'Not Sure', value: '6' },
    ];

    const timeFrameData = [
        { label: 'Less than 1 Month', value: '1' },
        { label: '1-3 Months', value: '2' },
        { label: '3-6 Months', value: '3' },
        { label: '6-12 Months', value: '4' },
        { label: '12+ Months', value: '5' },
        { label: 'Not Sure', value: '6' },
    ];

    const { push } = useRouter();

    const { executeRecaptcha } = useGoogleReCaptcha();

    const attachmentRef: any = useRef<string>('');

    const [inquiry, setInquiry] = useState<any>(initialValue);
    const [validation, setValidation] = useState(initialValidation);
    const [submitButtonDisable, setSubmitButtonDisable] = useState<boolean>(false);
    const [fileName, setFileName] = useState<string>('');
    const [phone, setPhone] = useState('');
    const [currentUrl, setCurrentUrl] = useState<string>('');
    const [countryCode, setCountryCode] = useState('');
    const [countryName, setCountryName] = useState('');
    const [selectedData, setSelectedData] = useState({
        label: 'Select',
        value: '',
    });
    const [budget, setBudget] = useState({
        label: 'Select',
        value: '',
    });
    const [timeFrame, setTimeFrame] = useState({
        label: 'Select',
        value: '',
    });

    const isLetsMeetUsa = requestFromPage === 'lets-meet-usa';

    const handleSubmitForm = useCallback(
        (e: any) => {
            e.preventDefault();
            if (isValidInquiry()) {
                setSubmitButtonDisable(true);

                if (!executeRecaptcha) {
                    setSubmitButtonDisable(false);
                    // Execute recaptcha not yet available - log only in development
                    if (process.env.NODE_ENV === 'development') {
                        console.log('Execute recaptcha not yet available');
                    }
                    setSubmitButtonDisable(false);
                    toast('Execute recaptcha not yet available!', {
                        autoClose: 2000,
                        type: 'error',
                        position: 'bottom-right',
                    });
                    return;
                }
                executeRecaptcha('enquiryFormSubmit')
                    .then(async (gReCaptchaToken: any) => {
                        await submitInquiry(gReCaptchaToken);
                    })
                    .catch((err) => {
                        setSubmitButtonDisable(false);
                        // Log error only in development to avoid information disclosure
                        if (process.env.NODE_ENV === 'development') {
                            console.log('err================', err);
                        }
                    });
            }
        },
        [executeRecaptcha, inquiry],
    );

    const isValidInquiry = () => {
        let isValid = true;
        const updatedValidation = { ...validation };

        if (isEmpty(inquiry.name.trim())) {
            isValid = false;
            updatedValidation['name'] = 'invalid';
        }

        if (isEmpty(inquiry.email) || !validateEmail(inquiry.email)) {
            isValid = false;
            updatedValidation['email'] = 'invalid';
        }

        const normalizedPhone = phone.replace(/\D/g, '');
        const phoneWithoutCountryCode = normalizedPhone.slice(countryCode.length);

        if (isEmpty(phoneWithoutCountryCode) || phoneWithoutCountryCode.length < 7) {
            isValid = false;
            updatedValidation['phone'] = 'invalid';
        } else {
            updatedValidation['phone'] = '';
        }

        if (!isLetsMeetUsa && isEmpty(inquiry.inquire_for)) {
            isValid = false;
            updatedValidation['inquire_for'] = 'invalid';
        }

        if (isEmpty(inquiry.message.trim())) {
            isValid = false;
            updatedValidation['message'] = 'invalid';
        }

        if (['apply_for_internship', 'apply_for_job'].includes(selectedData.value)) {
            if (isEmpty(attachmentRef) || isEmpty(attachmentRef.current) || isEmpty(attachmentRef.current.value)) {
                isValid = false;
                updatedValidation['attachment'] = 'invalid';
            }
        }

        if (['get_a_quote'].includes(selectedData.value)) {
            if (isEmpty(budget.value)) {
                isValid = false;
                updatedValidation['budget'] = 'invalid';
            }

            if (isEmpty(timeFrame.value)) {
                isValid = false;
                updatedValidation['time_frame'] = 'invalid';
            }
        }

        setValidation(updatedValidation);
        return isValid;
    };

    const submitInquiry = async (gReCaptchaToken: string) => {
        const inquiryFromData = new FormData();
        const payload: Record<string, any> = {};
        const normalizedPhone = phone.replace(/\D/g, '');
        const formattedPhone = normalizedPhone.slice(countryCode.length);

        if (isLetsMeetUsa) {
            payload.name = inquiry.name;
            payload.email = inquiry.email;
            payload.company_name = inquiry.company_name;
            payload.contact = formattedPhone;
            payload.zip_code = inquiry.zip_code || '';
            payload.date = inquiry.preferred_date;
            payload.message = inquiry.message;

            inquiryFromData.append('name', payload.name);
            inquiryFromData.append('email', payload.email);
            inquiryFromData.append('company_name', payload.company_name);
            inquiryFromData.append('contact', payload.contact);
            inquiryFromData.append('zip_code', payload.zip_code);
            inquiryFromData.append('date', payload.date);
            inquiryFromData.append('message', payload.message);
        } else {
            inquiryFromData.append('name', inquiry.name);
            inquiryFromData.append('email', inquiry.email);
            inquiryFromData.append('contact_number', formattedPhone);
            inquiryFromData.append('inquire_for', inquiry.inquire_for || (isLetsMeetUsa ? 'general_inquiry' : ''));

            if (!isLetsMeetUsa && ['get_a_quote'].includes(selectedData.value)) {
                if (!isEmpty(inquiry.budget)) {
                    inquiryFromData.append('budget', inquiry.budget);
                }

                if (!isEmpty(inquiry.timeFrame)) {
                    inquiryFromData.append('time_frame', inquiry.timeFrame);
                }
            }

            inquiryFromData.append('message', inquiry.message);
            inquiryFromData.append('company_name', inquiry.company_name || '');
            inquiryFromData.append('zip_code', inquiry.zip_code || '');
            inquiryFromData.append('preferred_date', inquiry.preferred_date || '');
        }
        inquiryFromData.append('country_code', `+${countryCode}`);
        inquiryFromData.append('country_name', countryName);
        inquiryFromData.append('request_from_page', requestFromPage || currentUrl);
        inquiryFromData.append(
            'attachment',
            !isEmpty(attachmentRef.current) && !isEmpty(attachmentRef.current.files[0]) ? attachmentRef.current.files[0] : '',
        );

        if (isLetsMeetUsa) {
            console.log('USA meeting payload:', payload);
            console.log('FormData entries:', Array.from(inquiryFromData.entries()));

            const route = routes.USA_MEETING;
            const res = await postRequest(route, inquiryFromData, {
                'Content-Type': 'multipart/form-data',
                recaptcha: gReCaptchaToken,
            });
            setSubmitButtonDisable(false);
            return;
        }

        const route = routes.CONTACT_INQUIRY_SUBMIT;
        const res = await postRequest(route, inquiryFromData, {
            'Content-Type': 'multipart/form-data',
            recaptcha: gReCaptchaToken,
        });

        if (res.status !== 201 || !res.data.status) {
            toast(`${res.data.message}`, {
                autoClose: 2000,
                type: 'error',
                position: 'bottom-right',
            });
        } else {
            clearRef();
            push('/thank-you');
        }
        setSubmitButtonDisable(false);
    };

    const clearRef = () => {
        attachmentRef.current.value = '';
        setPhone(countryCode);
        setValidation({ ...validation, ['email']: '' });
        setInquiry(initialValue);
    };

    const handleEmailValue = (e: any) => {
        const inputValue = e.target.value;
        if (!isEmpty(inputValue) && !validateEmail(inputValue)) {
            setValidation({ ...validation, ['email']: 'invalid' });
        }
    };

    const getCurrentUrl = () => {
        const currentUrl = window.location.href;
        setCurrentUrl(currentUrl);
    };

    useEffect(() => {
        getCurrentUrl();
    }, []);

    return (
        <>
            <Head>
                <title>Contact Inquiry</title>
                <meta property="og:title" content="My page title" key="title" />
            </Head>
            <div className={wrapperClassName}>
                <div
                    className="bg-white text-secondary dark:bg-[#3C456133] dark:border-[#ffffff33] dark:border dark:border-solid dark:text-light rounded-lg py-5 md:py-5 lg:py-10 px-5 md:px-5 lg:px-35"
                    data-aos="fade-up"
                    data-aos-duration={1000}
                    data-aos-delay={100}
                >
                    <h2 className="text-blue mb-2.5">{title}</h2>
                    <p className="mb-0 text-secondary fs-md fw-500 leading-[25px]">{subtitle}</p>
                    <hr className="opacity-10 bg-secondary my-5 dark:bg-[#3C4561] dark:opacity-100" />

                    <form className="grid md:grid-cols-2 gap-x-5 md:gap-x-7 gap-y-5" autoComplete="off">
                        <label className="block">
                            <span>Name</span>
                            <span className="text-red-600">*</span>
                            <input
                                type="text"
                                value={inquiry.name}
                                autoComplete="off"
                                className={clsx('form-control', validation.name)}
                                onChange={(e) => {
                                    setValidation({ ...validation, ['name']: '' });
                                    setInquiry({ ...inquiry, ['name']: e.target.value });
                                }}
                                placeholder="Type here..."
                            />
                        </label>
                        {isLetsMeetUsa && (
                            <label className="block">
                                <span>Company Name</span>
                                <input
                                    type="text"
                                    value={inquiry.company_name}
                                    autoComplete="off"
                                    className="form-control"
                                    onChange={(e) => {
                                        setInquiry({ ...inquiry, ['company_name']: e.target.value });
                                    }}
                                    placeholder="Type here..."
                                />
                            </label>
                        )}
                        <div>
                            <label className="block">
                                <span>Phone Number</span>
                                <span className="text-red-600">*</span>
                            </label>
                            <PhoneInput
                                country={'us'}
                                enableSearch={true}
                                value={phone}
                                countryCodeEditable={false}
                                inputStyle={{
                                    height: 40,
                                    width: '100%',
                                    borderRadius: 10,
                                    borderColor: validation.phone === 'invalid' ? 'red' : '',
                                }}
                                isValid={(value: string, country: any) => {
                                    return true;
                                }}
                                onChange={(phoneNumber: any, countryDetails: any) => {
                                    setCountryCode(countryDetails.countryCode);
                                    setCountryName(countryDetails.name);

                                    if (countryDetails.dialCode !== countryCode) {
                                        setPhone(countryDetails.dialCode);
                                        setInquiry({ ...inquiry, ['phone']: '' });
                                    } else {
                                        setPhone(phoneNumber);
                                        setInquiry({ ...inquiry, ['phone']: phoneNumber });
                                    }

                                    setValidation({ ...validation, ['phone']: '' });
                                }}
                            />
                        </div>
                        <label className="block">
                            <span>Email</span>
                            <span className="text-red-600">*</span>
                            <input
                                type="text"
                                autoComplete="off"
                                className={clsx('form-control', validation.email)}
                                onChange={(e) => {
                                    setValidation({ ...validation, ['email']: '' });
                                    setInquiry({ ...inquiry, ['email']: e.target.value });
                                }}
                                value={inquiry.email}
                                onBlur={handleEmailValue}
                                placeholder="Type here..."
                            />
                        </label>
                        {isLetsMeetUsa && (
                            <>
                                <label className="block">
                                    <span>Zip Code</span>
                                    <input
                                        type="text"
                                        value={inquiry.zip_code}
                                        autoComplete="off"
                                        className="form-control"
                                        onChange={(e) => {
                                            setInquiry({ ...inquiry, ['zip_code']: e.target.value });
                                        }}
                                        placeholder="Enter US Zip Code"
                                    />
                                </label>
                                <label className="block">
                                    <span>Preferred Date</span>
                                    <input
                                        type="date"
                                        value={inquiry.preferred_date}
                                        autoComplete="off"
                                        className={clsx('form-control')}
                                        onChange={(e) => {
                                            setInquiry({ ...inquiry, ['preferred_date']: e.target.value });
                                        }}
                                    />
                                </label>
                            </>
                        )}
                        {!isLetsMeetUsa && (
                            <label className="block">
                                <span>Inquire For</span>
                                <span className="text-red-600">*</span>
                                <CustomAsyncPaginateStatic
                                    data={data}
                                    selectedData={selectedData}
                                    setSelectedData={setSelectedData}
                                    handleChange={(e: any) => {
                                        attachmentRef.current.value = '';
                                        setValidation({
                                            ...validation,
                                            ['inquire_for']: '',
                                            ['attachment']: '',
                                            ['message']: '',
                                        });

                                        setInquiry({
                                            ...inquiry,
                                            ['inquire_for']: e.value,
                                            ['attachment']: '',
                                            ['message']: '',
                                        });

                                        if (e.value !== 'get_a_quote') {
                                            setBudget({ label: 'Select', value: '' });
                                            setTimeFrame({ label: 'Select', value: '' });
                                        }
                                    }}
                                    validation={clsx('rounded-10 mt-1', validation.inquire_for)}
                                    placeholder="Select"
                                />
                            </label>
                        )}
                        {!isLetsMeetUsa && ['get_a_quote'].includes(selectedData.value) && (
                            <>
                                <div className="">
                                    <label htmlFor="minmax-range" className="block">
                                        Budget
                                        <span className="text-red-600">*</span>
                                    </label>

                                    <CustomAsyncPaginateStatic
                                        data={budgetData}
                                        selectedData={budget}
                                        setSelectedData={setBudget}
                                        handleChange={(e: any) => {
                                            attachmentRef.current.value = '';
                                            setValidation({
                                                ...validation,
                                                ['budget']: '',
                                            });

                                            setInquiry({
                                                ...inquiry,
                                                ['budget']: e.value,
                                                ['attachment']: '',
                                                ['message']: '',
                                            });
                                        }}
                                        placeholder="Select Budget"
                                        validation={clsx('rounded-10 mt-1 dark:border-[#3C4561]', validation.budget)}
                                    />
                                </div>

                                <div>
                                    <div className="">
                                        <label htmlFor="minmax-range" className="block">
                                            Timeframe
                                            <span className="text-red-600">*</span>
                                        </label>

                                        <CustomAsyncPaginateStatic
                                            data={timeFrameData}
                                            selectedData={timeFrame}
                                            setSelectedData={setTimeFrame}
                                            handleChange={(e: any) => {
                                                attachmentRef.current.value = '';
                                                setValidation({
                                                    ...validation,
                                                    ['time_frame']: '',
                                                });

                                                setInquiry({
                                                    ...inquiry,
                                                    ['time_frame']: e.value,
                                                    ['attachment']: '',
                                                    ['message']: '',
                                                });
                                            }}
                                            validation={clsx('rounded-10 mt-1 dark:border-[#3C4561]', validation.time_frame)}
                                            placeholder="Select Timeframe"
                                            className={clsx('form-control', validation.message)}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        {isLetsMeetUsa && (
                            <div className="md:col-span-2">
                                <label className="block">
                                    <span>Message</span>
                                    <span className="text-red-600">*</span>
                                    <textarea
                                        onChange={(e) => {
                                            setValidation({ ...validation, ['message']: '' });
                                            setInquiry({ ...inquiry, ['message']: e.target.value });
                                        }}
                                        className={clsx('form-control', validation.message)}
                                        rows={3}
                                        value={inquiry.message}
                                        placeholder="Type here..."
                                    />
                                </label>
                            </div>
                        )}
                        {!isLetsMeetUsa && (
                            <div className="md:col-span-2">
                                <label className="block">
                                    <span>
                                        {['apply_for_internship', 'apply_for_job'].includes(selectedData.value)
                                            ? 'Your Profile Bio / Cover Letter'
                                            : 'Message'}
                                    </span>
                                    <span className="text-red-600">*</span>
                                    <textarea
                                        onChange={(e) => {
                                            setValidation({ ...validation, ['message']: '' });
                                            setInquiry({ ...inquiry, ['message']: e.target.value });
                                        }}
                                        className={clsx('form-control', validation.message)}
                                        rows={3}
                                        value={inquiry.message}
                                        placeholder="Type here..."
                                    />
                                </label>
                            </div>
                        )}
                        {!isLetsMeetUsa && (
                            <div className="md:col-span-2">
                                <label className="block"></label>
                                <span>
                                    {['apply_for_internship', 'apply_for_job'].includes(selectedData.value)
                                        ? 'Attach your Resume'
                                        : 'Attachment (Optional)'}{' '}
                                </span>
                                {['apply_for_internship', 'apply_for_job'].includes(selectedData.value) && (
                                    <span className="text-red-600">*</span>
                                )}

                                <div className={clsx('upldimg h-24 mt-1', validation.attachment)}>
                                    <label htmlFor="file-upload" className="custom-file-upload mb-0">
                                        {isEmpty(attachmentRef) ||
                                        isEmpty(attachmentRef.current) ||
                                        isEmpty(attachmentRef.current.value)
                                            ? 'Click to select or drop the file here'
                                            : fileName}
                                    </label>
                                    <input
                                        id="file-upload"
                                        ref={attachmentRef}
                                        type="file"
                                        onChange={(e: any) => {
                                            let allowedExtensions =
                                                /(\.jpg|\.jpeg|\.png|\.pdf|\.docx|\.doc|\.txt|\.xlsx|\.webp)$/i;
                                            if (allowedExtensions.exec(e.target.value)) {
                                                if (e.target.files.length > 0) {
                                                    setFileName(e.target.files[0].name);
                                                }
                                                setValidation({ ...validation, ['attachment']: '' });
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="md:col-span-2">
                            <hr className="opacity-10 bg-secondary my-0 dark:bg-[#3C4561] dark:opacity-100" />
                        </div>
                        <div className="md:col-span-2">
                            <button
                                type="button"
                                className="btn btn-primary cursor-pointer"
                                aria-labelledby="submitLabel"
                                onClick={handleSubmitForm}
                                disabled={submitButtonDisable}
                            >
                                {submitButtonDisable ? 'Please wait...' : 'Submit'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default ContactForm;
